// @flow
import React, { useState } from 'react';
import classNames from 'classnames';
import { find, get } from 'lodash';
import { Button, Icon } from '@elements';
import { fsPrintf } from '@helpers/helpers';
import { PlanFeatures, PlanPrice, PopularBadge, formatPlanAmount } from './PlanParts';

type Props = {
  plans: Array<{ type: string, plan: Object }>,
  pendingPlan: ?string,
  onSubscribe: Function,
  isOpening: Function,
  disabled: boolean,
  _: Function,
};

// Phones: every plan and price on one screen, the chosen plan's details below and a
// single "Assinar" bar pinned to the bottom.
const PlanPicker = ({ plans, pendingPlan, onSubscribe, isOpening, disabled, _ }: Props) => {
  const recommended = find(plans, ({ plan }) => plan.recommended) || plans[0];
  const [chosen, setChosen] = useState(pendingPlan || get(recommended, ['plan', 'plan']));
  const selected = find(plans, ({ plan }) => plan.plan === chosen) || recommended;

  if (!selected) return null;

  const { plan } = selected;
  const price = `${plan.currencySymbol} ${formatPlanAmount(plan)}`;

  return (
    <>
      <div className="planPicker" role="radiogroup" aria-label={_('label/pricing_title')}>
        {plans.map((option) => {
          const checked = option.plan.plan === plan.plan;
          return (
            <button
              key={option.type}
              type="button"
              role="radio"
              aria-checked={checked}
              className={classNames('planOption', { checked })}
              onClick={() => setChosen(option.plan.plan)}
            >
              <span className="planRadio" aria-hidden="true" />
              <span className="planOptionName">
                {option.plan.name}
                {option.plan.recommended && <PopularBadge _={_} />}
              </span>
              <PlanPrice plan={option.plan} small _={_} />
            </button>
          );
        })}
      </div>

      <section className="planDetails">
        <h2>{_('label/included_in', null, fsPrintf(plan.name))}</h2>
        <p className="planTagline">{_(`label/tagline/${selected.type}`)}</p>
        <PlanFeatures type={selected.type} _={_} />
      </section>

      {/* While this plan's checkout is open in the browser, the waiting panel leads instead */}
      {plan.plan !== pendingPlan && (
        <div className="checkoutBar">
          <Button
            className="primary"
            text={_('action/subscribe_plan', null, fsPrintf(plan.name, price))}
            isLoading={isOpening(plan.plan)}
            disabled={disabled}
            loadingMessage={_('label/opening_checkout')}
            onClick={() => onSubscribe(plan.plan)}
          />
          {/* Paying happens in the browser (no in-app purchase): say so before the user taps. */}
          <p>
            <Icon iconName="lock" size={12} />
            {_('label/web_checkout_short')}
          </p>
        </div>
      )}
    </>
  );
};

export default PlanPicker;
